export type StayPlanModule = "daycare" | "hotel";
export type StayPlanKind = "day_package" | "recurring";
export type RecurringUsageRule = "per_week" | "per_month";

export interface StayPlanTables {
  plans: string;
  dayPackages: string;
  recurringRules: string;
  contracts: string;
  charges: string;
  usages: string;
  adjustments: string;
}

/** As tabelas são gêmeas por módulo; todo o código acima delas é único. */
export const STAY_PLAN_TABLES: Record<StayPlanModule, StayPlanTables> = {
  daycare: {
    plans: "daycare_plans",
    dayPackages: "daycare_plan_day_packages",
    recurringRules: "daycare_plan_recurring_rules",
    contracts: "daycare_plan_contracts",
    charges: "daycare_plan_charges",
    usages: "stay_reservation_daycare_plan_usages",
    adjustments: "daycare_plan_balance_adjustments",
  },
  hotel: {
    plans: "hotel_plans",
    dayPackages: "hotel_plan_day_packages",
    recurringRules: "hotel_plan_recurring_rules",
    contracts: "hotel_plan_contracts",
    charges: "hotel_plan_charges",
    usages: "stay_reservation_hotel_plan_usages",
    adjustments: "hotel_plan_balance_adjustments",
  },
};

export interface DateRange {
  start: string;
  end: string;
}

function toUtcDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new Error("invalid_calendar_date");
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

function toIsoDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

function addDays(value: string, amount: number) {
  const date = toUtcDate(value);
  date.setUTCDate(date.getUTCDate() + amount);
  return toIsoDate(date);
}

/**
 * Mesmo dia do mês, N meses adiante, encurtado para o último dia quando o mês
 * de destino é mais curto (31/01 + 1 mês = 28/02).
 */
function addMonthsClamped(value: string, months: number) {
  const source = toUtcDate(value);
  const targetYear = source.getUTCFullYear();
  const targetMonth = source.getUTCMonth() + months;
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  return toIsoDate(new Date(Date.UTC(targetYear, targetMonth, Math.min(source.getUTCDate(), lastDay))));
}

/**
 * O ciclo de cobrança que contém `reference`, ancorado em `firstDueOn`.
 * Referência anterior ao primeiro vencimento devolve o primeiro ciclo.
 */
export function getBillingCycle(firstDueOn: string, reference: string): DateRange {
  let start = firstDueOn;
  let end = addDays(addMonthsClamped(firstDueOn, 1), -1);
  let guard = 0;

  while (reference > end && guard < 600) {
    guard += 1;
    start = addMonthsClamped(firstDueOn, guard);
    end = addDays(addMonthsClamped(firstDueOn, guard + 1), -1);
  }

  return { start, end };
}

export interface CycleQuotaInput {
  usageRule: RecurringUsageRule;
  usageLimit: number | null;
}

/** Cota do ciclo. `null` para `per_week` (ver `getWeeklyQuotaStatus`). */
export function getCycleQuota(input: CycleQuotaInput): number | null {
  if (input.usageRule !== "per_month") return null;
  return input.usageLimit ?? 0;
}

export interface WeekRange {
  start: string;
  end: string;
}

/** Segunda a domingo do calendário que contém `reference`. */
export function getCalendarWeek(reference: string): WeekRange {
  const date = toUtcDate(reference);
  const day = date.getUTCDay(); // 0=domingo..6=sábado
  const offsetToMonday = day === 0 ? -6 : 1 - day;
  const start = addDays(reference, offsetToMonday);
  return { start, end: addDays(start, 6) };
}

export interface WeeklyQuotaStatus {
  /** Segunda a domingo que a cota mede. O card rotula o período com ela. */
  week: WeekRange;
  usedThisWeek: number;
  /** Limite contratado do plano, antes de ajustes destinados à semana. */
  limit: number;
  /** Limite após ajustes manuais vinculados a esta semana. */
  effectiveLimit: number;
  /** Esta ação seria a última dentro da cota (medida antes dela). */
  isLastSlot: boolean;
  /** A cota já estava esgotada antes desta ação. */
  isOverLimit: boolean;
}

/** Só para plano recorrente `per_week`: cota validada pelo check-in/reserva real da semana, não por dia fixo. */
export function getWeeklyQuotaStatus(input: {
  usageLimit: number;
  effectiveLimit?: number;
  usages: Array<{ usedOn: string; units: number }>;
  reference: string;
}): WeeklyQuotaStatus {
  const week = getCalendarWeek(input.reference);
  const usedThisWeek = input.usages
    .filter((usage) => usage.usedOn >= week.start && usage.usedOn <= week.end)
    .reduce((total, usage) => total + usage.units, 0);

  const effectiveLimit = input.effectiveLimit ?? input.usageLimit;

  return {
    week,
    usedThisWeek,
    limit: input.usageLimit,
    effectiveLimit,
    isLastSlot: usedThisWeek === effectiveLimit - 1,
    isOverLimit: usedThisWeek >= effectiveLimit,
  };
}

export interface CarryoverSource {
  carryoverUnits: number;
  carryoverExpiresOn: string | null;
}

/** Saldo que um contrato novo herda do anterior do mesmo pet e módulo. */
export function inheritCarryover(previous: CarryoverSource | null, startsOn: string): number {
  if (!previous || !previous.carryoverExpiresOn) return 0;
  return startsOn <= previous.carryoverExpiresOn ? previous.carryoverUnits : 0;
}

/**
 * Primeiro vencimento de um contrato: o dia `billingDay` do mês de `startsOn`
 * quando ele ainda não passou, senão o mês seguinte. Mês curto encurta para o
 * último dia (dia 31 em fevereiro → 28/29).
 */
export function resolveFirstDueOn(startsOn: string, billingDay: number): string {
  if (!Number.isInteger(billingDay) || billingDay < 1 || billingDay > 31) {
    throw new Error("invalid_billing_day");
  }
  const start = toUtcDate(startsOn);
  const year = start.getUTCFullYear();
  const month = start.getUTCMonth();
  const dayInMonth = (targetMonth: number) => {
    const lastDay = new Date(Date.UTC(year, targetMonth + 1, 0)).getUTCDate();
    return Math.min(billingDay, lastDay);
  };
  const thisMonth = dayInMonth(month);
  if (thisMonth >= start.getUTCDate()) return toIsoDate(new Date(Date.UTC(year, month, thisMonth)));
  return toIsoDate(new Date(Date.UTC(year, month + 1, dayInMonth(month + 1))));
}

export type StayPlanFinancialStatus = "regular" | "due_soon" | "due_today" | "overdue";

function daysBetween(start: string, end: string) {
  return Math.round((toUtcDate(end).getTime() - toUtcDate(start).getTime()) / 86_400_000);
}

/**
 * Derivado, nunca gravado: comparar `next_due_on` com hoje é o que dispensa
 * qualquer job em background.
 */
export function getFinancialStatus(
  input: { nextDueOn: string | null; hasOverdueCharge: boolean },
  today: string,
): StayPlanFinancialStatus {
  if (input.hasOverdueCharge) return "overdue";
  if (!input.nextDueOn) return "regular";
  if (input.nextDueOn < today) return "overdue";
  if (input.nextDueOn === today) return "due_today";
  const distance = daysBetween(today, input.nextDueOn);
  return distance >= 1 && distance <= 7 ? "due_soon" : "regular";
}

export type StayPlanPaymentStatus = "paid" | "scheduled" | "awaiting" | "overdue" | "none";

export interface StayPlanChargeInput {
  dueOn: string;
  saleStatus: string;
  amount: number;
}

const VOIDED_SALE_STATUSES = new Set(["CANCELADA", "DEVOLVIDA"]);

/**
 * Eixo de dinheiro, separado de `getFinancialStatus` (que é vigência do plano).
 * Num pós-pago, "ainda não venceu" não é "está pago": vira `scheduled`.
 */
export function getStayPlanPaymentStatus(
  input: { billingTiming: "start" | "end"; charge: StayPlanChargeInput | null },
  today: string,
): StayPlanPaymentStatus {
  const { charge } = input;
  if (!charge) return "none";
  if (charge.saleStatus === "PAGA") return "paid";
  if (VOIDED_SALE_STATUSES.has(charge.saleStatus)) return "none";
  // `<`, não `<=`: no próprio dia do vencimento a cobrança ainda está no prazo
  // — vira atraso só no dia seguinte. Mesma régua de `getFinancialStatus`.
  if (charge.dueOn < today) return "overdue";
  return input.billingTiming === "end" ? "scheduled" : "awaiting";
}

export interface StayPlanBalanceInput {
  quota: number | null;
  carryoverUnits: number;
  /** Diárias dadas (ou tiradas) à mão neste ciclo. Expiram com ele. */
  adjustmentUnits: number;
  /** Unidades já debitadas por check-in dentro do ciclo. */
  checkins: number;
  /** Unidades seguradas por reservas futuras ainda não realizadas. */
  holds: number;
}

export interface StayPlanBalance {
  effective: number | null;
  projected: number | null;
}

export function getStayPlanBalance({ quota, carryoverUnits, adjustmentUnits, checkins, holds }: StayPlanBalanceInput): StayPlanBalance {
  if (quota === null) return { effective: null, projected: null };
  const effective = Math.max(0, quota + carryoverUnits + adjustmentUnits - checkins);
  return { effective, projected: Math.max(0, effective - holds) };
}

export interface StayPlanCarryoverInput {
  quota: number;
  carryoverUnits: number;
  adjustmentUnits: number;
  checkins: number;
}

/**
 * O que sobra de COTA no fim do ciclo — e só isso vira carryover.
 *
 * Ordem de consumo: carryover → ajuste → cota. O ajuste é um extra que expira
 * com o ciclo, então nunca alimenta o carryover. Esta função é espelhada em SQL
 * dentro de roll_stay_plan_cycles; mudar uma sem a outra faz a virada de ciclo
 * divergir da tela.
 */
export function getStayPlanCarryover({ quota, carryoverUnits, adjustmentUnits, checkins }: StayPlanCarryoverInput): number {
  const absorvidoPelosExtras = Math.max(0, carryoverUnits + adjustmentUnits);
  const contraCota = Math.max(0, checkins - absorvidoPelosExtras);
  return Math.max(0, quota - contraCota);
}

export type StayPlanAlertKind = "overdue" | "due_soon" | "last_cycle_unit" | "last_plan_unit" | "last_week_unit" | "week_quota_exceeded";

export interface StayPlanAlertInput {
  kind: StayPlanKind;
  financialStatus: StayPlanFinancialStatus;
  /** Saldo previsto do ciclo; `null` em pacote de diárias. */
  projectedBalance: number | null;
  /** Diárias restantes de um `day_package`; `null` em recorrente. */
  remainingDays: number | null;
  weeklyQuota?: WeeklyQuotaStatus | null;
}

/** Alertas independentes: podem aparecer juntos, e cada um pede uma ação diferente. */
export function getStayPlanAlerts(input: StayPlanAlertInput): StayPlanAlertKind[] {
  const alerts: StayPlanAlertKind[] = [];

  if (input.financialStatus === "overdue") alerts.push("overdue");
  else if (input.financialStatus === "due_soon" || input.financialStatus === "due_today") alerts.push("due_soon");

  if (input.kind === "recurring" && input.projectedBalance === 1) alerts.push("last_cycle_unit");
  if (input.kind === "day_package" && input.remainingDays === 1) alerts.push("last_plan_unit");

  if (input.weeklyQuota?.isOverLimit) alerts.push("week_quota_exceeded");
  else if (input.weeklyQuota?.isLastSlot) alerts.push("last_week_unit");

  return alerts;
}

/**
 * A mensalidade já foi lançada como linha na comanda aberta — pagar essa
 * comanda resolve o vencimento, então repetir o aviso "vencida"/"vence em"
 * ao lado dela só confunde. Casamento exato com o nome gerado ao contratar
 * (ver `PlanAndDaysSection`), já que a linha não guarda o `contractId`.
 */
export function planChargeIsInComanda({
  planName,
  membershipLineLabel,
  lines,
}: {
  planName: string;
  membershipLineLabel: string;
  lines: Array<{ itemName: string }>;
}): boolean {
  const expected = `${membershipLineLabel}: ${planName}`;
  return lines.some((line) => line.itemName === expected);
}

/** Remove os avisos de vencimento quando a cobrança já está na comanda aberta. */
export function filterStayPlanAlertsForComanda(
  alerts: StayPlanAlertKind[],
  isChargeInComanda: boolean,
): StayPlanAlertKind[] {
  if (!isChargeInComanda) return alerts;
  return alerts.filter((alert) => alert !== "overdue" && alert !== "due_soon");
}
